import React from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import NfcRoundedIcon from '@mui/icons-material/NfcRounded'
import MerchantToolShell, { shellStyles } from './MerchantToolShell'
import { useAuth } from '../../contexts/AuthContext'
import { useLanguage } from '../../contexts/LanguageContext'

export default function MerchantReceiveQr() {
    const { user } = useAuth()
    const { lang } = useLanguage()
    const isFr = lang === 'fr'
    const merchantName = user?.business_name || user?.name || 'MojoPayr Business'
    const merchantId = user?.id || 'merchant'
    const country = user?.business_country || user?.country || 'Sierra Leone'
    const qrValue = `mojopayr://pay?merchant_id=${merchantId}`

    const handleCopyPayload = async () => {
        try {
            await navigator.clipboard.writeText(qrValue)
        } catch {
            // no-op fallback for older browsers
        }
    }

    return (
        <MerchantToolShell
            eyebrow={isFr ? 'Recevoir paiement' : 'Receive Payment'}
            title={isFr ? 'Recevoir paiement' : 'Receive Payment'}
            subtitle={
                isFr
                    ? 'Affichez un QR marchand ou copiez le payload NFC pour que le client ouvre le paiement en un geste.'
                    : 'Show a merchant QR or copy the NFC payload so the customer can open payment in one step.'
            }
            chips={[
                isFr ? 'QR pret' : 'QR ready',
                isFr ? 'NFC pret' : 'NFC ready',
            ]}
            left={
                <Stack spacing={2}>
                    <Box
                        sx={{
                            p: 3,
                            borderRadius: 5,
                            background: 'linear-gradient(135deg, rgba(42,27,102,1) 0%, rgba(91,46,255,1) 100%)',
                        }}
                    >
                        <Typography sx={{ color: '#FFFFFF', fontWeight: 900, fontSize: 30 }}>
                            {merchantName}
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.72)', mt: 0.6 }}>
                            {merchantId} • {country}
                        </Typography>
                        <Stack direction="row" spacing={1.2} sx={{ mt: 2.2 }}>
                            <Box sx={{ ...shellStyles.softPanel, px: 2, py: 1 }}>
                                <Typography sx={{ fontWeight: 800 }}>{isFr ? 'QR pret' : 'QR ready'}</Typography>
                            </Box>
                            <Box sx={{ ...shellStyles.softPanel, px: 2, py: 1 }}>
                                <Typography sx={{ fontWeight: 800 }}>{isFr ? 'NFC pret' : 'NFC ready'}</Typography>
                            </Box>
                        </Stack>
                    </Box>

                    <Box sx={{ ...shellStyles.panel, p: 3 }}>
                        <Stack spacing={2} alignItems="center">
                            <Box
                                sx={{
                                    width: 280,
                                    maxWidth: '100%',
                                    aspectRatio: '1 / 1',
                                    borderRadius: 5,
                                    bgcolor: '#FFFFFF',
                                    display: 'grid',
                                    placeItems: 'center',
                                    color: '#0B0620',
                                }}
                            >
                                <QrCode2RoundedIcon sx={{ fontSize: 150 }} />
                            </Box>
                            <Typography sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 500 }}>
                                {isFr
                                    ? 'Les clients peuvent scanner le QR ou utiliser un tag MojoPayr NFC pour ouvrir le paiement marchand.'
                                    : 'Customers can scan the QR or tap a MojoPayr NFC tag to open merchant payment.'}
                            </Typography>
                        </Stack>
                    </Box>
                </Stack>
            }
            right={
                <Stack spacing={2}>
                    <Box sx={shellStyles.softPanel}>
                        <Typography sx={{ fontWeight: 800, mb: 1.2 }}>
                            {isFr ? 'Tag NFC marchand' : 'Merchant NFC tag'}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 2 }}>
                            {isFr
                                ? 'Ecrivez ce payload sur un tag NFC pour que les clients ouvrent le paiement marchand en un tap.'
                                : 'Write this payload to an NFC tag so customers can open merchant pay in one tap.'}
                        </Typography>
                        <Stack spacing={1.2}>
                            <Button
                                variant="outlined"
                                startIcon={<ContentCopyRoundedIcon />}
                                onClick={handleCopyPayload}
                                sx={{
                                    color: '#FFFFFF',
                                    borderColor: 'rgba(183,166,255,0.34)',
                                    borderRadius: 4,
                                    textTransform: 'none',
                                    fontWeight: 800,
                                }}
                            >
                                {isFr ? 'Copier payload NFC' : 'Copy NFC Payload'}
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<NfcRoundedIcon />}
                                sx={{
                                    bgcolor: '#FF8A00',
                                    color: '#0B0620',
                                    borderRadius: 4,
                                    textTransform: 'none',
                                    fontWeight: 900,
                                    '&:hover': { bgcolor: '#ff9c2a' },
                                }}
                            >
                                {isFr ? 'Ecrire tag NFC' : 'Write NFC Tag'}
                            </Button>
                        </Stack>
                    </Box>

                    <Box sx={shellStyles.softPanel}>
                        <Typography sx={{ fontWeight: 800, mb: 1 }}>
                            {isFr ? 'Reference marchande' : 'Merchant reference'}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', fontSize: 13, wordBreak: 'break-all' }}>
                            {qrValue}
                        </Typography>
                    </Box>
                </Stack>
            }
        />
    )
}
